"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TourStep, TourPlacement } from "./tour-steps";

interface TourTooltipProps {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onFinish: () => void;
  targetRect: DOMRect;
}

const ARROW_SIZE = 8;
const TOOLTIP_OFFSET = 12;

export function TourTooltip({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onFinish,
  targetRect,
}: TourTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; arrow: number } | null>(null);
  const [actualPlacement, setActualPlacement] = useState<TourPlacement>(step.placement);

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === totalSteps - 1;

  const calculatePosition = useCallback(() => {
    if (!tooltipRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let placement = step.placement;

    const calculate = (p: TourPlacement) => {
      switch (p) {
        case "top":
          return {
            top: targetRect.top - tooltipRect.height - TOOLTIP_OFFSET,
            left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
          };
        case "bottom":
          return {
            top: targetRect.bottom + TOOLTIP_OFFSET,
            left: targetRect.left + targetRect.width / 2 - tooltipRect.width / 2,
          };
        case "left":
          return {
            top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
            left: targetRect.left - tooltipRect.width - TOOLTIP_OFFSET,
          };
        case "right":
          return {
            top: targetRect.top + targetRect.height / 2 - tooltipRect.height / 2,
            left: targetRect.right + TOOLTIP_OFFSET,
          };
      }
    };

    let pos = calculate(placement);

    const fitsInViewport = (t: number, l: number) => {
      return (
        t >= 10 &&
        l >= 10 &&
        t + tooltipRect.height <= viewportHeight - 10 &&
        l + tooltipRect.width <= viewportWidth - 10
      );
    };

    if (!fitsInViewport(pos.top, pos.left)) {
      const opposite: Record<TourPlacement, TourPlacement> = {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
      };
      const oppPos = calculate(opposite[placement]);
      if (fitsInViewport(oppPos.top, oppPos.left)) {
        placement = opposite[placement];
        pos = oppPos;
      }
    }

    const top = Math.max(10, Math.min(pos.top, viewportHeight - tooltipRect.height - 10));
    const left = Math.max(10, Math.min(pos.left, viewportWidth - tooltipRect.width - 10));

    const alongWidth = placement === "top" || placement === "bottom";
    const targetCentre = alongWidth
      ? (Math.max(targetRect.left, 0) + Math.min(targetRect.right, viewportWidth)) / 2 - left
      : (Math.max(targetRect.top, 0) + Math.min(targetRect.bottom, viewportHeight)) / 2 - top;
    const span = alongWidth ? tooltipRect.width : tooltipRect.height;
    const arrow = Math.max(ARROW_SIZE * 2, Math.min(targetCentre, span - ARROW_SIZE * 2));

    setPosition({ top, left, arrow });
    setActualPlacement(placement);
  }, [targetRect, step.placement]);

  useLayoutEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  useEffect(() => {
    const timer = setTimeout(() => {
      nextButtonRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [stepIndex]);

  useEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    const focusableElements = tooltip.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    tooltip.addEventListener("keydown", handleTabKey);
    return () => tooltip.removeEventListener("keydown", handleTabKey);
  }, [stepIndex]);

  const arrowStyles = (): React.CSSProperties => {
    const base: React.CSSProperties = {
      position: "absolute",
      width: 0,
      height: 0,
      borderStyle: "solid",
    };

    switch (actualPlacement) {
      case "top":
        return {
          ...base,
          bottom: -ARROW_SIZE,
          left: position?.arrow,
          transform: "translateX(-50%)",
          borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px 0 ${ARROW_SIZE}px`,
          borderColor: "var(--surface-overlay) transparent transparent transparent",
        };
      case "bottom":
        return {
          ...base,
          top: -ARROW_SIZE,
          left: position?.arrow,
          transform: "translateX(-50%)",
          borderWidth: `0 ${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px`,
          borderColor: "transparent transparent var(--surface-overlay) transparent",
        };
      case "left":
        return {
          ...base,
          right: -ARROW_SIZE,
          top: position?.arrow,
          transform: "translateY(-50%)",
          borderWidth: `${ARROW_SIZE}px 0 ${ARROW_SIZE}px ${ARROW_SIZE}px`,
          borderColor: "transparent transparent transparent var(--surface-overlay)",
        };
      case "right":
        return {
          ...base,
          left: -ARROW_SIZE,
          top: position?.arrow,
          transform: "translateY(-50%)",
          borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px 0`,
          borderColor: "transparent var(--surface-overlay) transparent transparent",
        };
    }
  };

  const titleId = `tour-title-${stepIndex}`;
  const descId = `tour-desc-${stepIndex}`;

  return (
    <section
      ref={tooltipRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className={cn(
        "pointer-events-auto fixed z-[9999] w-[320px] max-w-[calc(100vw-20px)]",
        "transition-opacity duration-300 ease-out",
        position ? "opacity-100" : "opacity-0"
      )}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
      }}
    >
      <div className="relative rounded-panel border border-white/10 bg-surface-overlay shadow-2xl overflow-hidden">
        <div className="h-0.5 w-full bg-primary" />

        <div className="p-4">
          <button type="button"
            onClick={onSkip}
            className="absolute right-3 top-3.5 min-h-[44px] min-w-[44px] flex items-center justify-center text-white/40 hover:text-white/70 transition-colors"
            aria-label="Skip tour"
          >
            <X className="size-4" />
          </button>

          <div className="flex items-center gap-1 mb-3 pr-12" aria-hidden="true">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "h-1 w-1 rounded-full transition-all duration-200",
                  i === stepIndex
                    ? "w-4 bg-primary"
                    : i < stepIndex
                      ? "bg-primary/50"
                      : "bg-white/20"
                )}
              />
            ))}
            <span className="ml-auto text-[10px] text-white/30 tabular-nums">
              {stepIndex + 1}/{totalSteps}
            </span>
          </div>

          <h3 id={titleId} className="text-sm font-semibold text-white mb-1.5">
            {step.title}
          </h3>

          <p id={descId} className="text-sm text-white/60 leading-relaxed mb-4">
            {step.content}
          </p>

          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <button type="button"
                onClick={onPrev}
                className="flex items-center gap-1 text-sm text-white/50 hover:text-white/80 transition-colors"
              >
                <ChevronLeft className="size-3.5" />
                Back
              </button>
            )}

            <div className="flex-1" />

            <button type="button"
              onClick={onSkip}
              className="text-sm text-white/40 hover:text-white/60 transition-colors px-2"
            >
              Skip
            </button>

            {isLastStep ? (
              <button type="button"
                ref={nextButtonRef}
                onClick={onFinish}
                className="bg-white text-black text-sm font-medium px-4 py-1.5 rounded-md hover:bg-white/90 transition-colors"
              >
                Finish tour
              </button>
            ) : (
              <button type="button"
                ref={nextButtonRef}
                onClick={onNext}
                className="flex items-center gap-1 bg-white text-black text-sm font-medium px-3 py-1.5 rounded-md hover:bg-white/90 transition-colors"
              >
                Next
                <ChevronRight className="size-3.5" />
              </button>
            )}
          </div>
        </div>

      </div>

      <div aria-hidden="true" style={arrowStyles()} />
    </section>
  );
}
